import { useMemo, useState } from "react";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { useServerTable } from "../../../components/table/tableFeatures";
import { MdRotateLeft } from "react-icons/md";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { resetTable } from "../../../components/table/utils";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { useStandardEntityMetrics } from "../../merchandising/components/tables/metricUtils";
import { Button } from "../../../components/core/basic/Button";
import { RT8FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { useGetDSPProductPaginated } from "../hooks/useDSPProductPaginatedQuery";
import type { DSPProductRow } from "../../../zodios/apiDefinitions/api/dsp/productDatasets";
import DSPProductExpander from "./ProductExpander";
import ExternalProductLink from "../../../components/custom/merchandise/ExternalProductLink";
import NoImage from "../../../assets/images/utility/no_image.png";
import { getURLPrefix } from "../../../utils/getUrlPrefix";
import { snakeCaseToSentenceCase, toTitleCase } from "../../../utils/formatText";
import { useDSPProductConversionTypeSelectOptions } from "../utils/dspLifecycleFilters";
import { Switch } from "../../../components/fields/base/Switch";

const columnHelper = createColumnBuilder<DSPProductRow>();

type Props = {
  adGroupId: string | number;
  featuredAsinsFilter?: boolean;
};

const DSPProductsTable = ({ adGroupId, featuredAsinsFilter = false }: Props) => {
  const { t } = useTranslation(["dsp", "common"]);
  const [showParentAsins, setShowParentAsins] = useState(false);
  const standardMetricIds = useStandardEntityMetrics("dspProduct");
  const conversionTypeSelectOptions = useDSPProductConversionTypeSelectOptions();

  const reportGranularity = showParentAsins ? "Parent ASIN level" : "ASIN level";
  const persistKey = featuredAsinsFilter ? "dspFeaturedProductsTable" : "dspProductsTable";

  const { state, initialState, setters } = usePersistedTableState({
    persistKey,
    initialState: {
      sorting: [{ id: "dsp_total_detail_page_views", desc: true }],
    },
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor("asin", {
        id: "asin",
        header: t(($) => $.dsp["ASIN"]),
        minSize: 300,
        size: 450,
        maxSize: 600,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const { asin, product_name, report_granularity } = row.original;
          const URL_PREFIX = getURLPrefix();
          const image = `${URL_PREFIX}/api/productimage/?asin=${asin}`;

          return (
            <div className={css({ display: "flex", fontWeight: "medium" })}>
              <div
                className={css({
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  flexShrink: 0,
                  width: "50px",
                  height: "50px",
                  marginRight: "10px",
                })}
                style={{
                  background: `url(${image}) no-repeat center / contain, url("${NoImage}") no-repeat center / contain`,
                }}
                draggable={false}
              />
              <div className={css({ overflow: "hidden", display: "flex", flexDirection: "column" })}>
                <span
                  className={css({
                    fontWeight: "medium",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    display: "block",
                  })}
                >
                  {product_name}
                </span>
                <div>
                  <i className={css({ paddingRight: "small", fontWeight: "light", color: "#666", fontSize: "xs" })}>
                    {asin}
                  </i>
                  <ExternalProductLink asin={asin ?? ""} />
                  {report_granularity === "Parent ASIN level" && (
                    <span
                      className={css({
                        borderRadius: "5px",
                        marginLeft: "small",
                        paddingX: "small",
                        paddingY: "2px",
                        fontSize: "xs",
                        fontWeight: "semibold",
                        backgroundColor: "trellisPalette.7",
                        color: "white",
                      })}
                    >
                      {t(($) => $.dsp["Parent ASIN"])}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        },
        meta: {
          columnSelectorOptions: { label: t(($) => $.dsp["ASIN"]) },
        },
      }),
      columnHelper.accessor("conversion_type", {
        id: "conversion_type",
        header: t(($) => $.dsp["Conversion Type"]),
        size: 170,
        cell: ({ getValue }) => {
          const value = getValue();
          return toTitleCase(snakeCaseToSentenceCase(typeof value === "string" ? value : ""));
        },
        meta: {
          columnFilterOptions: { format: "select" },
          columnSelectorOptions: { label: t(($) => $.dsp["Conversion Type"]) },
        },
      }),
      ...standardMetricIds.map((id) => columnHelper.metric(id)),
    ],
    [standardMetricIds, t],
  );

  const debouncedSearch = useDebounce(state.globalFilter, 500);

  const getQueryOptions = useGetDSPProductPaginated({
    lineItemId: adGroupId,
    reportGranularity,
    featuredAsin: featuredAsinsFilter ? "Y" : null,
  });
  const productQuery = useBlockWindowQuery(getQueryOptions, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: state.columnFilters,
    search: debouncedSearch,
  });

  const tableData = useMemo<DSPProductRow[]>(() => productQuery.data?.data ?? [], [productQuery.data]);

  const table = useServerTable({
    data: tableData,
    columns,
    initialState,
    state,
    ...setters,
    rowCount: productQuery.data?.count ?? undefined,
    getRowId: (row) => (row.asin ?? "") + "_" + (row.conversion_type ?? ""),
    getRowCanExpand: () => true,
    meta: {
      showSkeleton: productQuery.isFetching && (!productQuery.data || productQuery.isPlaceholderData),
      columnFilterSelectOptions: {
        conversion_type: conversionTypeSelectOptions,
      },
    },
  });

  return (
    <>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
            {!featuredAsinsFilter && (
              <HStack gap={2} alignItems={"center"} className={css({ fontSize: "sm" })}>
                <span>{t(($) => $.dsp["Child ASINs"])}</span>
                <Switch
                  isSelected={showParentAsins}
                  onChange={() => {
                    setShowParentAsins(!showParentAsins);
                    table.setPageIndex(0);
                  }}
                />
                <span>{t(($) => $.dsp["Parent ASINs"])}</span>
              </HStack>
            )}
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table
        table={table}
        className={css({ minWidth: "100%" })}
        renderExpander={(row) => <DSPProductExpander row={row} lineItemId={adGroupId} />}
      />
    </>
  );
};

export default DSPProductsTable;
